import React, { useEffect, useState } from "react";
import { useAuthState } from "../../components/AuthProvider";
import handleResponse from "../../utils/handleFetchResponse";
import fetchConfig from "../../utils/fetchConfig";
import Container from "../../components/Container";
import "./OverrideSummary.css";

// Extract a flat address string from a FHIR address object
const formatAddress = (addr) => {
  if (!addr) return "";
  const line = addr.line?.join(", ") || "";
  const city = addr.city || "";
  const state = addr.state || "";
  const postal = addr.postalCode || "";
  return `${line}, ${city}, ${state} ${postal}`
    .replace(/^[, ]+|[, ]+$/g, "")
    .trim();
};

// Extract full name string from a FHIR name object
const formatName = (nameObj) => {
  if (!nameObj) return "";
  const given = nameObj.given?.join(" ") || "";
  const family = nameObj.family || "";
  return `${given} ${family}`.trim();
};

// Get the attrCode from a FHIR element's extensions
const getAttrCode = (element) => {
  if (!element?.extension) return "N/A";
  const ext = element.extension.find(
    (e) =>
      e.url ===
      "https://github.optum.com/pages/HealthcareMatching/hcm-microsite/fhir-extensions/imdm/#attrCode",
  );
  return ext?.valueString || "N/A";
};

// Diff two FHIR Patient resources and produce summary rows
const diffFhirPatients = (currentPatient, newPatient, recordId) => {
  const diffs = [];

  // Diff names (by attrCode)
  const currentNames = currentPatient?.name || [];
  const newNames = newPatient?.name || [];
  for (const newName of newNames) {
    const newAttr = getAttrCode(newName);
    const currentName = currentNames.find((n) => getAttrCode(n) === newAttr);
    const currentVal = formatName(currentName);
    const newVal = formatName(newName);
    if (currentVal !== newVal) {
      diffs.push({
        recordId,
        attribute: "Name",
        attrCode: newAttr,
        action: newVal ? "Replace" : "Inactivate",
        originalValue: currentVal,
        newValue: newVal,
      });
    }
  }

  // Diff addresses (by attrCode)
  const currentAddrs = currentPatient?.address || [];
  const newAddrs = newPatient?.address || [];
  for (const newAddr of newAddrs) {
    const newAttr = getAttrCode(newAddr);
    const currentAddr = currentAddrs.find((a) => getAttrCode(a) === newAttr);
    const currentVal = formatAddress(currentAddr);
    const newVal = newAddr.use === "old" ? "" : formatAddress(newAddr);
    const origVal = currentAddr?.use === "old" ? "" : currentVal;
    if (origVal !== newVal) {
      diffs.push({
        recordId,
        attribute: "Address",
        attrCode: newAttr,
        action: newVal ? "Replace" : "Inactivate",
        originalValue: origVal,
        newValue: newVal,
      });
    }
  }

  // Diff telecom (by system + attrCode)
  const currentTelecoms = currentPatient?.telecom || [];
  const newTelecoms = newPatient?.telecom || [];
  for (const newTel of newTelecoms) {
    const newAttr = getAttrCode(newTel);
    const currentTel = currentTelecoms.find(
      (t) => t.system === newTel.system && getAttrCode(t) === newAttr,
    );
    const currentVal = currentTel?.value || "";
    const newVal = newTel.value || "";
    if (currentVal !== newVal) {
      diffs.push({
        recordId,
        attribute: newTel.system === "phone" ? "Phone" : "Email",
        attrCode: newAttr,
        action: newVal ? "Replace" : "Inactivate",
        originalValue: currentVal,
        newValue: newVal,
      });
    }
  }

  // Diff birthDate
  if ((currentPatient?.birthDate || "") !== (newPatient?.birthDate || "")) {
    diffs.push({
      recordId,
      attribute: "Birth Date",
      attrCode: "N/A",
      action: newPatient?.birthDate ? "Replace" : "Inactivate",
      originalValue: currentPatient?.birthDate || "",
      newValue: newPatient?.birthDate || "",
    });
  }

  // Diff gender
  if ((currentPatient?.gender || "") !== (newPatient?.gender || "")) {
    diffs.push({
      recordId,
      attribute: "Gender",
      attrCode: "N/A",
      action: newPatient?.gender ? "Replace" : "Inactivate",
      originalValue: currentPatient?.gender || "",
      newValue: newPatient?.gender || "",
    });
  }

  return diffs;
};

export default function OverrideSummary() {
  const [summaryData, setSummaryData] = useState([]);
  const [goldenId, setGoldenId] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const { user } = useAuthState();
  const { selectedClient } = user.preferences;
  const userId = user.username;

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const summaryKey = urlParams.get("summaryKey");
    const urlGoldenId = urlParams.get("goldenId");

    if (urlGoldenId) {
      setGoldenId(urlGoldenId);
    }

    if (!summaryKey) {
      setLoading(false);
      return;
    }

    const storedData = localStorage.getItem(summaryKey);
    if (!storedData) {
      setLoading(false);
      return;
    }

    let requestPayload;
    try {
      requestPayload = JSON.parse(storedData);
      localStorage.removeItem(summaryKey);
    } catch (e) {
      console.error("Error parsing override_request:", e);
      setLoading(false);
      return;
    }

    // Extract sourceId from first patient
    let sourceId = "";
    try {
      if (requestPayload?.patients?.[0]) {
        const firstPatient = JSON.parse(requestPayload.patients[0]);
        sourceId = firstPatient?.meta?.source || "";
      }
    } catch (e) {
      console.error("Error parsing patient for sourceId:", e);
    }

    // Call override API in preview mode
    const overrideUrl = `${process.env.REACT_APP_CLIENT_IDENTITY_SERVICE_DOMAIN}/patientsource/override`;
    const overrideConfig = {
      method: "PUT",
      body: JSON.stringify(requestPayload, null, 0),
      credentials: "include",
      headers: {
        clientId: selectedClient.oid,
        "Content-Type": "application/json",
        userId: userId,
        preview: true,
        sourceId: sourceId,
      },
    };

    fetch(
      ...fetchConfig(overrideUrl, "/overridePreviewMock.json", overrideConfig),
    )
      .then(handleResponse)
      .then((response) => {
        const rawData = response.data?.map || response.data;

        // Parse recordUpdates — the post-override state of each record
        const recordUpdatesList = rawData?.recordUpdates?.myArrayList || [];
        const newRecordsByKey = {};
        for (const ru of recordUpdatesList) {
          const rec = ru.map;
          const recKey = `${rec.source_system}|${rec.source_entity_id}`;
          try {
            const fhirPatient =
              typeof rec.demographics === "string"
                ? JSON.parse(rec.demographics)
                : rec.demographics;
            newRecordsByKey[recKey] = fhirPatient;
          } catch (e) {
            console.error("Error parsing recordUpdate demographics:", e);
          }
        }

        if (Object.keys(newRecordsByKey).length === 0) {
          setSummaryData([]);
          setLoading(false);
          return;
        }

        // Determine target golden ID for matcher call
        const aggregatesList = rawData?.aggregates?.myArrayList || [];
        const aggregatesToDeleteList =
          rawData?.aggregatesToDelete?.myArrayList || [];
        const focusDeleted = aggregatesToDeleteList.some((item) =>
          item.endsWith(`|${urlGoldenId}`),
        );
        let targetGoldenId = urlGoldenId;
        if (focusDeleted) {
          for (const agg of aggregatesList) {
            const aggMap = agg.map;
            if (aggMap.golden_id !== urlGoldenId) {
              targetGoldenId = aggMap.golden_id;
              break;
            }
          }
        }

        // Call matcher API to get current records
        const matcherUrl = `${process.env.REACT_APP_CLIENT_IDENTITY_SERVICE_DOMAIN}/patient/match?include_related_identities=false`;
        const matcherBody = {
          patient: {
            resourceType: "Patient",
            link: [{ other: { reference: targetGoldenId }, type: "seealso" }],
          },
        };
        const matcherConfig = {
          method: "POST",
          body: JSON.stringify(matcherBody),
          credentials: "include",
          headers: {
            clientId: selectedClient.oid,
            "Content-Type": "application/json",
            userId: userId,
          },
        };

        fetch(...fetchConfig(matcherUrl, "/closeMatches.json", matcherConfig))
          .then(handleResponse)
          .then((matcherResponse) => {
            const matcherData = matcherResponse.data;
            const currentRecordsByKey = {};

            if (matcherData?.resourceType === "Bundle" && matcherData?.entry) {
              for (const entry of matcherData.entry) {
                const patient = entry.resource;
                if (
                  patient?.resourceType === "Patient" &&
                  patient.meta?.source
                ) {
                  const key = `${patient.meta.source}|${patient.id}`;
                  currentRecordsByKey[key] = patient;
                }
              }
            }

            // Diff each changed record
            const allDiffs = [];
            for (const [recKey, newPatient] of Object.entries(
              newRecordsByKey,
            )) {
              const currentPatient = currentRecordsByKey[recKey] || {};
              const recordDiffs = diffFhirPatients(
                currentPatient,
                newPatient,
                recKey,
              );
              allDiffs.push(...recordDiffs);
            }

            setSummaryData(allDiffs);
            setLoading(false);
          })
          .catch((matcherErr) => {
            console.warn(
              "Matcher API failed, falling back to override_request parsing:",
              matcherErr,
            );
            // Fallback: parse override_request directly
            const parsed = parseOverrideRequestFallback(requestPayload);
            setSummaryData(parsed);
            setLoading(false);
          });
      })
      .catch((e) => {
        console.error("Override preview API error:", e);
        // Fallback: parse override_request directly
        const parsed = parseOverrideRequestFallback(requestPayload);
        setSummaryData(parsed);
        setError("Preview API unavailable. Showing changes from request data.");
        setLoading(false);
      });
  }, [selectedClient.oid, userId]);

  // Fallback parser when the preview API is unavailable
  const parseOverrideRequestFallback = (override_request) => {
    if (!override_request?.patients || override_request.patients.length === 0) {
      return [];
    }

    const overrideSummary = [];

    override_request.patients.forEach((patientStr) => {
      try {
        const patient =
          typeof patientStr === "string" ? JSON.parse(patientStr) : patientStr;
        const recordId = `${patient.meta?.source}|${patient.id}`;

        // Address overrides
        if (patient.address && Array.isArray(patient.address)) {
          patient.address.forEach((addr) => {
            const overrideExt = addr.extension?.find(
              (ext) =>
                ext.url ===
                "https://github.optum.com/pages/HealthcareMatching/hcm-microsite/fhir-extensions/#override",
            );
            if (overrideExt && overrideExt.valueAddress) {
              const attrCode = getAttrCode(addr);
              const originalValue =
                overrideExt.valueAddress.use !== "old"
                  ? formatAddress(overrideExt.valueAddress)
                  : "";
              const newValue = addr.use !== "old" ? formatAddress(addr) : "";
              overrideSummary.push({
                recordId,
                attribute: "Address",
                attrCode,
                action: newValue ? "Replace" : "Inactivate",
                originalValue,
                newValue,
              });
            }
          });
        }

        // Name overrides
        if (patient.name && Array.isArray(patient.name)) {
          patient.name.forEach((name) => {
            const overrideExt = name.extension?.find(
              (ext) =>
                ext.url ===
                "https://github.optum.com/pages/HealthcareMatching/hcm-microsite/fhir-extensions/#override",
            );
            if (overrideExt && overrideExt.valueHumanName) {
              const attrCode = getAttrCode(name);
              const originalName = formatName(overrideExt.valueHumanName);
              const newName = formatName(name);
              overrideSummary.push({
                recordId,
                attribute: "Name",
                attrCode,
                action: newName ? "Replace" : "Inactivate",
                originalValue: originalName,
                newValue: newName,
              });
            }
          });
        }

        // Identifier overrides
        if (patient.identifier && Array.isArray(patient.identifier)) {
          patient.identifier.forEach((identifier) => {
            const overrideExt = identifier.extension?.find(
              (ext) =>
                ext.url ===
                "https://github.optum.com/pages/HealthcareMatching/hcm-microsite/fhir-extensions/#override",
            );
            if (overrideExt && overrideExt.valueIdentifier) {
              overrideSummary.push({
                recordId,
                attribute: "Identifier",
                attrCode: identifier.system || "N/A",
                action: identifier.value ? "Replace" : "Inactivate",
                originalValue: overrideExt.valueIdentifier.value || "",
                newValue: identifier.value || "",
              });
            }
          });
        }

        // Telecom overrides
        if (patient.telecom && Array.isArray(patient.telecom)) {
          patient.telecom.forEach((telecom) => {
            const overrideExt = telecom.extension?.find(
              (ext) =>
                ext.url ===
                "https://github.optum.com/pages/HealthcareMatching/hcm-microsite/fhir-extensions/#override",
            );
            if (overrideExt && overrideExt.valueContactPoint) {
              const attrCode = getAttrCode(telecom);
              overrideSummary.push({
                recordId,
                attribute: telecom.system === "phone" ? "Phone" : "Email",
                attrCode,
                action: telecom.value ? "Replace" : "Inactivate",
                originalValue: overrideExt.valueContactPoint.value || "",
                newValue: telecom.value || "",
              });
            }
          });
        }

        // BirthDate override
        if (patient.birthDate) {
          const birthDateExt = patient.extension?.find(
            (ext) =>
              ext.url?.includes("#override-birthDate") ||
              ext.url?.includes("#overrideBirthDate"),
          );
          if (birthDateExt && birthDateExt.valueDate) {
            overrideSummary.push({
              recordId,
              attribute: "Birth Date",
              attrCode: "N/A",
              action: patient.birthDate ? "Replace" : "Inactivate",
              originalValue: birthDateExt.valueDate,
              newValue: patient.birthDate,
            });
          }
        }

        // Gender override
        if (patient.gender) {
          const genderExt = patient.extension?.find(
            (ext) =>
              ext.url ===
              "https://github.optum.com/pages/HealthcareMatching/hcm-microsite/fhir-extensions/#override-gender",
          );
          if (genderExt && genderExt.valueCode) {
            overrideSummary.push({
              recordId,
              attribute: "Gender",
              attrCode: "N/A",
              action: patient.gender ? "Replace" : "Inactivate",
              originalValue: genderExt.valueCode,
              newValue: patient.gender,
            });
          }
        }
      } catch (e) {
        console.error("Error parsing patient override:", e);
      }
    });

    return overrideSummary;
  };

  if (loading) {
    return (
      <Container>
        <div className="override-summary-loading">
          <div className="spinner" />
          <p>Loading override summary...</p>
        </div>
      </Container>
    );
  }

  return (
    <Container>
      <div className="override-summary-page">
        <div className="override-summary-page-header">
          <div className="override-summary-page-title-row">
            <h1 className="override-summary-page-title">Override Summary</h1>
            {goldenId && (
              <span className="override-summary-eid-badge">
                EID: {goldenId}
              </span>
            )}
          </div>
          <p className="override-summary-page-description">
            The following changes will be applied to the records.
          </p>
          {error && <p className="override-summary-fallback-notice">{error}</p>}
        </div>

        {summaryData.length === 0 ? (
          <div className="override-summary-empty">
            <svg
              className="override-summary-empty-icon"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            <h2>No override data available</h2>
            <p>The override request does not contain any parseable changes.</p>
          </div>
        ) : (
          <>
            <div className="override-summary-page-table-wrapper">
              <table className="override-summary-page-table">
                <thead>
                  <tr>
                    <th>Record ID</th>
                    <th>Attribute</th>
                    <th>AttrCode</th>
                    <th>Action</th>
                    <th>Current Value</th>
                    <th>New Value</th>
                  </tr>
                </thead>
                <tbody>
                  {summaryData.map((item, idx) => (
                    <tr key={idx}>
                      <td>
                        <code className="record-id-code">{item.recordId}</code>
                      </td>
                      <td>
                        <strong>{item.attribute}</strong>
                      </td>
                      <td className="attr-code-cell">{item.attrCode}</td>
                      <td>
                        <span
                          className={`action-badge action-${item.action.toLowerCase()}`}
                        >
                          {item.action}
                        </span>
                      </td>
                      <td className="value-cell original-value">
                        {item.originalValue || "—"}
                      </td>
                      <td className="value-cell new-value">
                        {item.newValue || "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="override-summary-page-footer">
              <div className="override-summary-count-badge">
                <strong>{summaryData.length}</strong> override
                {summaryData.length !== 1 ? "s" : ""} will be applied
              </div>
            </div>
          </>
        )}
      </div>
    </Container>
  );
}
